import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Person } from './api';

export function useUrlState<Key extends string>(defaults: Record<Key, string | null>) {
  const [params, setParams] = useSearchParams();
  const fallbacks = useRef(defaults);
  fallbacks.current = defaults;
  const pending = useRef(params);
  const rendered = useRef(params.toString());
  if (rendered.current !== params.toString()) {
    rendered.current = params.toString();
    pending.current = params;
  }
  const values = Object.fromEntries(Object.entries(defaults).map(([key, fallback]) => [
    key, params.has(key) ? params.get(key) || null : fallback,
  ])) as Record<Key, string | null>;
  const update = useCallback((changes: Partial<Record<Key, string | null>>) => {
    const next = new URLSearchParams(pending.current);
    for (const [key, value] of Object.entries(fallbacks.current)) {
      if (!next.has(key) && typeof value === 'string') next.set(key, value);
    }
    for (const [key, value] of Object.entries(changes)) {
      if (typeof value === 'string') next.set(key, value);
      else next.delete(key);
    }
    pending.current = next;
    setParams(next);
  }, [setParams]);
  return [values, update] as const;
}

export function personFromUrl(people: Person[], value: string | null): string | null {
  if (!value) return null;
  return people.find(person => person.pid === value ||
    person.email_alias?.replace(/@.*$/, '').toLowerCase() === value.replace(/@.*$/, '').toLowerCase())?.pid ?? null;
}

export function personUrlValue(people: Person[], pid: string | null): string | null {
  if (!pid) return null;
  return people.find(person => person.pid === pid)?.email_alias?.replace(/@.*$/, '') || pid;
}

const PICKER_KEYS: Record<string, string[]> = {
  manager: ['manager'], coverage: ['rep', 'view', 'contact'], paths: ['from', 'to'], town: ['rep', 'metro'],
};

export function workspacePickerSearch(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  for (const key of PICKER_KEYS[pathname.split('/')[3] ?? ''] ?? []) params.delete(key);
  const value = params.toString();
  return value ? `?${value}` : '';
}
